<script setup lang="ts">
/**
 * Slide-out AI chat panel.
 * Streams Claude responses via SSE. Toggled by FloatingActionBar.
 */
import { ref, nextTick, watch, onMounted } from 'vue'
import { api, sanitizeHtml, toDisplayDate } from '@claimshield/shared'
import { useAuthStore } from '@claimshield/shared'
import MarkdownIt from 'markdown-it'
import { GUIDES } from '../views/guides/guides'

const md = new MarkdownIt({ breaks: true, linkify: true })

const props = defineProps<{ open: boolean }>()
const emit = defineEmits<{ 'update:open': [value: boolean] }>()

const authStore = useAuthStore()

const inputText = ref('')
const isStreaming = ref(false)
const messagesContainer = ref<HTMLElement | null>(null)

interface Message {
  role: 'user' | 'assistant'
  content: string
  toolActivity?: string
}

const messages = ref<Message[]>([])

// --- Conversation history (V2 persistence) ---
const conversationId = ref<string>('')
const showHistory = ref(false)
const loadingHistory = ref(false)

interface ConversationSummary {
  conversation_id: string
  title: string
  message_count: number
  last_message_at: string | null
  client_id: string | null
  client_name: string | null
}
const conversations = ref<ConversationSummary[]>([])

const STORAGE_KEY = 'cs_chat_active'

// The guide catalog travels with every request rather than being duplicated in
// Sentinel: `guides.ts` is the single source of truth, so adding a guide here
// makes the assistant aware of it with no backend change and nothing to drift.
// Titles and descriptions only — the guide bodies are static HTML in this
// bundle, which the API cannot read.
const GUIDE_CATALOG = GUIDES.map((g) => ({
  slug: g.slug,
  title: g.title,
  description: g.description,
  category: g.category,
}))

function newConversationId(): string {
  try {
    if (crypto?.randomUUID) return crypto.randomUUID()
  } catch {}
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

// Persist the active chat so a page refresh doesn't wipe it (durable browsing/
// resume of past chats lives server-side in PG via the conversations API).
//
// The saved chat is stamped with the user it belongs to. Until 2026-10-02 it
// was not, and logout does not clear this key, so the next person to sign in
// on the same browser resumed the previous user's conversation (ops portal
// audit V4). A chat saved by anyone else, or by nobody, is dropped on restore.
function currentUserId(): string | null {
  return authStore.user?.id ? String(authStore.user.id) : null
}

function persistActive() {
  const userId = currentUserId()
  if (!userId) return
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ userId, conversationId: conversationId.value, messages: messages.value })
    )
  } catch {}
}

function restoreActive() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return
    const saved = JSON.parse(raw)
    const userId = currentUserId()
    if (!userId || saved?.userId !== userId) {
      localStorage.removeItem(STORAGE_KEY)
      return
    }
    if (saved?.conversationId) conversationId.value = saved.conversationId
    if (Array.isArray(saved?.messages)) messages.value = saved.messages
  } catch {}
}

// Signing out, or another user signing in without a reload, empties the panel.
watch(
  () => currentUserId(),
  (now, before) => {
    if (now === before) return
    messages.value = []
    conversationId.value = ''
    conversations.value = []
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch {}
    if (now) restoreActive()
  }
)

onMounted(async () => {
  restoreActive()
  // An empty panel opens onto the consultant's recent chats rather than onto
  // nothing: the blank slate was the one place the persistence was invisible.
  if (messages.value.length === 0) await loadConversations()
})

async function loadConversations() {
  loadingHistory.value = true
  try {
    const res = await api.get('/v1/chat/conversations')
    if (res.data?.success && Array.isArray(res.data.data)) {
      conversations.value = res.data.data
    } else {
      console.warn('[ChatPanel] Unexpected conversations response:', res.data)
    }
  } catch (e) {
    console.error('[ChatPanel] Failed to load conversations:', e)
  } finally {
    loadingHistory.value = false
  }
}

async function openHistory() {
  showHistory.value = true
  await loadConversations()
}

async function resumeConversation(cid: string) {
  loadingHistory.value = true
  try {
    const res = await api.get(`/v1/chat/conversations/${cid}`)
    if (res.data?.success && res.data.data) {
      messages.value = (res.data.data.messages || []).map(
        (m: { role: 'user' | 'assistant'; content: string }) => ({ role: m.role, content: m.content })
      )
      conversationId.value = cid
      showHistory.value = false
      persistActive()
      await scrollToBottom()
    }
  } catch (e) {
    console.error('[ChatPanel] Failed to resume conversation:', e)
  } finally {
    loadingHistory.value = false
  }
}

function formatWhen(iso: string | null): string {
  if (!iso) return ''
  try {
    return toDisplayDate(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  } catch {
    return ''
  }
}

// Read client context fresh from sessionStorage (not reactive — read at send time)
function getClientContext(): Record<string, string> {
  try {
    const id = sessionStorage.getItem('dashboard_clientId')
    if (id) {
      const name = sessionStorage.getItem('dashboard_clientName')
      return { client_id: id, ...(name ? { client_name: name } : {}) }
    }
  } catch {}
  return {}
}

// Reactive snapshot for the context badge display (updates when panel opens)
const clientContext = ref<Record<string, string>>(getClientContext())

// Refresh client context when panel opens (sessionStorage isn't reactive)
watch(() => props.open, (isOpen) => {
  if (isOpen) {
    clientContext.value = getClientContext()
    // Re-read on open: another tab (or another turn) may have added chats since
    // this component mounted.
    if (messages.value.length === 0) loadConversations()
  }
})

function resetChat() {
  messages.value = []
  inputText.value = ''
  conversationId.value = ''  // next send mints a fresh conversation
  showHistory.value = false
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {}
  // The chat just left is now one of the recent ones.
  loadConversations()
}

async function scrollToBottom() {
  await nextTick()
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
  }
}

async function sendMessage() {
  const text = inputText.value.trim()
  if (!text || isStreaming.value) return

  // Add user message
  messages.value.push({ role: 'user', content: text })
  inputText.value = ''
  if (!conversationId.value) conversationId.value = newConversationId()
  persistActive()
  await scrollToBottom()

  // Add placeholder for assistant response
  messages.value.push({ role: 'assistant', content: '', toolActivity: undefined })
  isStreaming.value = true

  // We just pushed the placeholder above, so the last entry is the
  // assistant message we're streaming into — guaranteed to exist for
  // the lifetime of this function.
  const assistantMsg = messages.value[messages.value.length - 1]!

  try {
    // Build request body — history excludes current user msg + assistant placeholder
    const history = messages.value
      .slice(0, -2)
      .filter(m => m.content)
      .map(m => ({ role: m.role, content: m.content }))

    // Read client context fresh (not from cached ref)
    const context = getClientContext()
    clientContext.value = context // update badge too
    const body = JSON.stringify({
      message: text,
      history,
      context,
      conversation_id: conversationId.value,
      guides: GUIDE_CATALOG,
    })

    // Get auth token — uses same key as auth store
    const token = authStore.token
    const baseUrl = api.defaults.baseURL || '/api'

    const response = await fetch(`${baseUrl}/v1/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body,
    })

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }

    const reader = response.body?.getReader()
    if (!reader) throw new Error('No response body')

    const decoder = new TextDecoder()
    let buffer = ''

    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })

      // Parse SSE events from buffer
      const lines = buffer.split('\n')
      buffer = lines.pop() || ''

      let eventType = ''
      for (const line of lines) {
        if (line.startsWith('event: ')) {
          eventType = line.slice(7).trim()
        } else if (line.startsWith('data: ')) {
          const data = JSON.parse(line.slice(6))

          if (eventType === 'token') {
            assistantMsg.content += data.text
            await scrollToBottom()
          } else if (eventType === 'tool_use') {
            assistantMsg.toolActivity = `Searching...`
          } else if (eventType === 'done') {
            assistantMsg.content = data.full_message
            assistantMsg.toolActivity = undefined
          } else if (eventType === 'error') {
            assistantMsg.content = data.message
            assistantMsg.toolActivity = undefined
          }

          eventType = ''
        }
      }
    }
  } catch (err) {
    // Only show error if we didn't already receive a complete response
    if (!assistantMsg.content) {
      assistantMsg.content =
        'Failed to connect. Please try again.'
    }
    assistantMsg.toolActivity = undefined
  } finally {
    isStreaming.value = false
    persistActive()
    await scrollToBottom()
  }
}

function handleKeydown(e: KeyboardEvent) {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    sendMessage()
  }
}
</script>

<template>
  <!-- Slide-out panel -->
  <transition name="slide">
    <div
      v-if="props.open"
      class="fixed top-0 right-0 h-full w-[420px] bg-white shadow-2xl z-40 flex flex-col"
    >
      <!-- Header -->
      <div class="flex items-center justify-between px-4 py-3 bg-secondary-50">
        <span class="font-semibold text-lg text-primary-800">ClaimShield Assistant</span>
        <div class="flex gap-1">
          <PButton
            icon="pi pi-history"
            text
            rounded
            size="small"
            v-tooltip="'Previous chats'"
            @click="showHistory ? (showHistory = false) : openHistory()"
            :disabled="isStreaming"
          />
          <PButton
            icon="pi pi-refresh"
            text
            rounded
            size="small"
            v-tooltip="'New chat'"
            @click="resetChat"
            :disabled="isStreaming"
          />
          <PButton
            icon="pi pi-times"
            text
            rounded
            size="small"
            @click="emit('update:open', false)"
          />
        </div>
      </div>

      <!-- Context badge -->
      <div v-if="clientContext.client_id" class="px-4 py-2 bg-secondary-50">
        <PTag :value="clientContext.client_name || 'Client selected'" icon="pi pi-building" severity="info" />
      </div>

      <!-- Previous chats -->
      <div
        v-if="showHistory"
        class="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-2 bg-white"
      >
        <div class="flex items-center justify-between mb-1">
          <span class="text-sm font-semibold text-secondary-700">Previous chats</span>
          <PButton icon="pi pi-arrow-left" text size="small" label="Back" @click="showHistory = false" />
        </div>
        <div v-if="loadingHistory" class="text-center text-secondary-400 mt-6">
          <i class="pi pi-spin pi-spinner text-xl" />
        </div>
        <div v-else-if="conversations.length === 0" class="text-center text-secondary-400 mt-6 text-sm">
          No previous chats yet.
        </div>
        <button
          v-for="c in conversations"
          :key="c.conversation_id"
          @click="resumeConversation(c.conversation_id)"
          class="text-left w-full rounded-[10px] bg-secondary-50 px-3 py-2 hover:bg-secondary-100 transition-colors"
        >
          <div class="text-sm text-secondary-900 font-medium truncate">{{ c.title }}</div>
          <div class="text-xs text-secondary-500 flex gap-2 mt-0.5">
            <span v-if="c.client_name" class="truncate">{{ c.client_name }}</span>
            <span>{{ c.message_count }} msg{{ c.message_count === 1 ? '' : 's' }}</span>
            <span v-if="c.last_message_at">{{ formatWhen(c.last_message_at) }}</span>
          </div>
        </button>
      </div>

      <!-- Messages -->
      <div
        v-show="!showHistory"
        ref="messagesContainer"
        class="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-[18px] bg-white"
      >
        <!-- Empty panel: the consultant's recent chats, not a blank slate -->
        <div v-if="messages.length === 0" class="flex flex-col gap-3">
          <div class="text-center text-secondary-400 mt-4">
            <i class="pi pi-comment text-3xl mb-2 block" />
            <p class="text-sm">Ask me anything about your clients, drivers, or compliance.</p>
          </div>

          <div v-if="loadingHistory" class="text-center text-secondary-400 mt-2">
            <i class="pi pi-spin pi-spinner" />
          </div>

          <template v-else-if="conversations.length">
            <div class="flex items-center justify-between mt-2">
              <span class="text-xs font-semibold uppercase tracking-wide text-secondary-500">
                Pick up where you left off
              </span>
              <PButton
                v-if="conversations.length > 5"
                label="All chats"
                text
                size="small"
                @click="openHistory"
              />
            </div>
            <button
              v-for="c in conversations.slice(0, 5)"
              :key="c.conversation_id"
              @click="resumeConversation(c.conversation_id)"
              class="text-left w-full rounded-[10px] bg-secondary-50 px-3 py-2 hover:bg-secondary-100 transition-colors"
            >
              <div class="text-sm text-secondary-900 font-medium truncate">{{ c.title }}</div>
              <div class="text-xs text-secondary-500 flex gap-2 mt-0.5">
                <span v-if="c.client_name" class="truncate">{{ c.client_name }}</span>
                <span>{{ c.message_count }} msg{{ c.message_count === 1 ? '' : 's' }}</span>
                <span v-if="c.last_message_at">{{ formatWhen(c.last_message_at) }}</span>
              </div>
            </button>
          </template>
        </div>

        <template v-for="(msg, i) in messages" :key="i">
          <!-- User turn: compact right-aligned bubble -->
          <div v-if="msg.role === 'user'" class="self-end max-w-[82%]">
            <div class="bg-primary-700 text-white text-sm leading-relaxed px-3 py-2 rounded-[14px] rounded-br-[4px] whitespace-pre-wrap">{{ msg.content }}</div>
          </div>

          <!-- Assistant turn: avatar + name, then the answer bare beneath it.
               The user's bubble is what makes this read as a conversation;
               the reply gets the full column so its tables and lists have
               room, which they do not in a 420px panel if boxed. -->
          <div v-else class="self-stretch">
            <div class="flex items-center gap-2 mb-1">
              <span class="w-[22px] h-[22px] rounded-md bg-gradient-to-br from-primary-700 to-primary-600 text-white text-[11px] font-bold grid place-items-center shrink-0">CS</span>
              <span class="text-xs font-semibold text-primary-800">Assistant</span>
              <!-- Tool activity chip -->
              <span
                v-if="msg.toolActivity"
                class="ml-auto text-[11px] text-secondary-500 bg-secondary-100 rounded-full px-2 py-0.5 inline-flex items-center gap-1.5"
              >
                <i class="pi pi-spin pi-spinner text-[10px]" />
                {{ msg.toolActivity }}
              </span>
            </div>

            <!-- No surface behind the reply, on purpose (Ahmed, 2026-08-21).
                 The avatar and the user's own bubble already establish who is
                 speaking; boxing the answer as well just narrows the tables
                 and adds a second frame inside a 420px panel. -->
            <div
              v-if="msg.content"
              v-html="sanitizeHtml(md.render(msg.content))"
              class="chat-md prose prose-sm max-w-none"
            />

            <!-- Streaming cursor -->
            <span
              v-if="isStreaming && i === messages.length - 1 && !msg.toolActivity && !msg.content"
              class="inline-block w-2 h-4 bg-secondary-400 animate-pulse"
            />
          </div>
        </template>
      </div>

      <!-- Input area -->
      <!-- Composer. One rounded field with the controls INSIDE it, not a
           text input with two buttons parked beside it on a grey bar —
           that arrangement reads as a search bar, which is the wrong
           promise to make about a thing you hold a conversation with. -->
      <div v-if="!showHistory" class="chat-dock px-3 pb-3 pt-2 bg-white">
        <div class="chat-composer flex gap-1 items-end bg-secondary-100 rounded-[20px] pl-3.5 pr-1.5 py-1">
          <PTextarea
            v-model="inputText"
            placeholder="Message the assistant…"
            rows="1"
            autoResize
            class="flex-1"
            @keydown="handleKeydown"
            :disabled="isStreaming"
          />
          <!-- Voice input — slot reserved, wiring lands with the mic enhancement -->
          <PButton
            icon="pi pi-microphone"
            text
            rounded
            severity="secondary"
            disabled
            v-tooltip="'Voice input — coming soon'"
            aria-label="Voice input (coming soon)"
          />
          <PButton
            icon="pi pi-send"
            rounded
            @click="sendMessage"
            :loading="isStreaming"
            :disabled="!inputText.trim() || isStreaming"
            aria-label="Send"
          />
        </div>
      </div>
    </div>
  </transition>
</template>

<style scoped>
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.25s ease;
}
.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}

/* ---------------------------------------------------------------------------
 * Assistant message typography.
 *
 * These rules are NOT optional polish. ops does not install
 * @tailwindcss/typography — nothing registers it in tailwind.config.js and the
 * compiled bundle carries zero `not-prose`/`prose-sm` selectors — so the
 * `prose prose-sm` classes on the content div resolve to nothing at all.
 * Tailwind's preflight meanwhile sets `ol,ul,menu{list-style:none;padding:0}`
 * and `a{color:inherit;text-decoration:inherit}`, so without what follows the
 * assistant's markdown loses its bullets, its numbers, its indentation and any
 * sign that a link is a link. `views/admin/DocsView.vue` hand-writes the same
 * stack for the same reason; this is that precedent, scoped to the panel.
 *
 * `.prose` stays on the element deliberately: `html.dark-mode .prose …` rules
 * exist elsewhere in ops, and dropping the class would regress dark mode.
 *
 * Sizes come from the product scale in knowledge/brand/tokens.md:93 —
 * "title 20/600 · section 16/600 · body 14-15/1.55 · label 13-14/500 · caption 12".
 * Nothing here is invented. Markdown's six heading levels collapse into two,
 * because a 420px chat reply is not a document and the old mapping inverted:
 * h4 rendered at 13px, under the 15px paragraph it introduced.
 * ------------------------------------------------------------------------- */

/* ── The composer ──────────────────────────────────────────────────────────
 * No borders anywhere in this panel (Ahmed, 2026-08-21). Two consequences,
 * both handled here rather than left to chance:
 *
 * 1. A border was doing the "you can type here" work. Tone takes over — the
 *    composer is a filled surface against the white transcript. Removing the
 *    border without replacing the affordance would be minimalism, not
 *    simplicity, which is the trade the Apple foundations warn about.
 * 2. A border was also separating the transcript from the composer. That is
 *    replaced by a scroll-edge fade (`.chat-dock::before`) — content dissolves
 *    into the chrome instead of being cut off by a rule.
 *
 * Focus is a material change, not an outline: the surface lifts to white and
 * gains a soft shadow, so the field reads as coming forward. Guarded for
 * reduced motion, and it degrades to an instant swap rather than nothing.
 */
.chat-dock {
  position: relative;
}

/* Content fades into the dock rather than meeting a rule. */
.chat-dock::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: 100%;
  height: 20px;
  pointer-events: none;
  background: linear-gradient(to top, theme('colors.white'), transparent);
}

.chat-composer {
  transition: background-color 160ms ease, box-shadow 160ms ease;
}

.chat-composer:focus-within {
  background: theme('colors.white');
  box-shadow: 0 1px 2px rgb(15 23 42 / 0.06), 0 8px 24px rgb(15 23 42 / 0.08);
}

@media (prefers-reduced-motion: reduce) {
  .chat-composer {
    transition: none;
  }
}

/* The textarea sits inside the composer's own surface, so it must not draw a
 * field of its own — two nested fields is the look we just removed. */
.chat-composer :deep(.p-textarea) {
  border: 0;
  outline: 0;
  box-shadow: none;
  background: transparent;
  padding: 7px 0;
  min-height: 0;
  max-height: 160px;
  resize: none;
  font-size: 14px;
  line-height: 1.5;
}

.chat-composer :deep(.p-textarea:enabled:focus) {
  border: 0;
  outline: 0;
  box-shadow: none;
}

.chat-composer :deep(.p-button) {
  width: 32px;
  height: 32px;
  flex: none;
}

.chat-md {
  font-size: 15px;
  line-height: 1.55;
  color: theme('colors.secondary.800');
}

.chat-md :deep(p) {
  margin: 6px 0;
}

.chat-md :deep(p:first-child) {
  margin-top: 0;
}

.chat-md :deep(p:last-child) {
  margin-bottom: 0;
}

/* Headings — two levels, both at or above body size. */
.chat-md :deep(h1),
.chat-md :deep(h2),
.chat-md :deep(h3) {
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
  letter-spacing: normal;
  color: theme('colors.secondary.900');
  margin: 14px 0 6px;
}

.chat-md :deep(h4),
.chat-md :deep(h5),
.chat-md :deep(h6) {
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;
  letter-spacing: normal;
  color: theme('colors.primary.800');
  margin: 12px 0 4px;
}

.chat-md :deep(:is(h1, h2, h3, h4, h5, h6):first-child) {
  margin-top: 0;
}

/* Lists — the markers preflight strips. */
.chat-md :deep(ul),
.chat-md :deep(ol) {
  margin: 6px 0;
  padding-left: 20px;
}

.chat-md :deep(ul) {
  list-style: disc;
}

.chat-md :deep(ol) {
  list-style: decimal;
}

.chat-md :deep(li) {
  margin: 3px 0;
  padding-left: 2px;
}

.chat-md :deep(li)::marker {
  color: theme('colors.secondary.400');
}

.chat-md :deep(li > ul),
.chat-md :deep(li > ol) {
  margin: 3px 0 0;
  padding-left: 16px;
}

.chat-md :deep(li > ul) {
  list-style: circle;
}

/* Emphasis. */
.chat-md :deep(strong) {
  font-weight: 600;
  color: theme('colors.secondary.900');
}

.chat-md :deep(em) {
  font-style: italic;
}

/* Links — preflight strips colour and underline from every anchor. */
.chat-md :deep(a) {
  color: theme('colors.primary.600');
  text-decoration: underline;
  text-decoration-color: theme('colors.primary.300');
  text-underline-offset: 2px;
  overflow-wrap: anywhere;
}

.chat-md :deep(a:hover) {
  color: theme('colors.primary.700');
  text-decoration-color: theme('colors.primary.500');
}

/* Quotes. */
.chat-md :deep(blockquote) {
  margin: 8px 0;
  padding: 2px 0 2px 12px;
  border-left: 2px solid theme('colors.primary.200');
  color: theme('colors.secondary.600');
}

.chat-md :deep(blockquote p) {
  margin: 3px 0;
}

.chat-md :deep(hr) {
  margin: 12px 0;
  border: 0;
  border-top: 1px solid theme('colors.secondary.200');
}

/* Code. Canon reserves JetBrains Mono for code and file paths; it is not
 * loaded as a webfont here, so the stack degrades to the platform mono. */
.chat-md :deep(code) {
  font-family: 'JetBrains Mono', ui-monospace, 'Courier New', monospace;
  font-size: 13px;
  background: theme('colors.secondary.100');
  padding: 1px 5px;
  border-radius: 4px;
  overflow-wrap: anywhere;
}

.chat-md :deep(pre) {
  margin: 8px 0;
  padding: 10px 12px;
  background: theme('colors.secondary.50');
  border: 1px solid theme('colors.secondary.200');
  border-radius: 6px;
  overflow-x: auto;
}

.chat-md :deep(pre code) {
  background: none;
  padding: 0;
  font-size: 12.5px;
  line-height: 1.5;
}

/* Tables. A 420px panel cannot widen, so let a wide one scroll rather than
 * crush its columns. */
.chat-md :deep(table) {
  width: 100%;
  margin: 8px 0;
  font-size: 13px;
  border-collapse: collapse;
}

.chat-md :deep(th) {
  text-align: left;
  font-weight: 600;
  color: theme('colors.secondary.500');
  padding: 4px 8px 4px 0;
  border-bottom: 1px solid theme('colors.secondary.300');
}

.chat-md :deep(td) {
  padding: 4px 8px 4px 0;
  border-bottom: 1px solid theme('colors.secondary.200');
  vertical-align: top;
}

.chat-md :deep(tr:last-child td) {
  border-bottom: 0;
}
</style>
